import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all ' +
  'disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-bg whitespace-nowrap';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-gradient-to-r from-tech to-ai text-white shadow-sm hover:brightness-110 active:brightness-95',
  secondary: 'bg-surface-2 text-fg hover:bg-border',
  outline: 'border border-border bg-transparent text-fg hover:border-tech hover:text-tech',
  ghost: 'bg-transparent text-muted hover:bg-surface-2 hover:text-fg',
  danger: 'bg-danger text-white hover:brightness-110',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-9 w-9 p-0',
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & CommonProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export type ButtonLinkProps = LinkProps & CommonProps;

export function ButtonLink({
  className,
  variant = 'primary',
  size = 'md',
  ...props
}: ButtonLinkProps) {
  return <Link className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...props} />;
}

export function ExternalButtonLink({
  className,
  variant = 'outline',
  size = 'md',
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & CommonProps) {
  return (
    <a
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      target="_blank"
      rel="noreferrer noopener"
      {...props}
    />
  );
}
